import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { FOCStatusBadge, FOC_STATUS_TONE } from "../components/etams/StatusBadge";
import { Plus, Gift } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDateTime } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";
import PemilihCari from "../components/etams/PemilihCari";
import { cariCustomer, labelCustomer, ketCustomer } from "../lib/sumberPilihan";

//: Label saringan status — untuk chip saringan aktif.
const LABEL_STATUS = {
  PENDING: "Menunggu Owner", APPROVED: "Disetujui", REJECTED: "Ditolak", CANCELLED: "Dibatalkan",
};

// Rincian item ditampilkan sebagai baris biasa, bukan JSON mentah (K200 butir 6).
function rincianTeks(v) {
  const satu = (x) => (x && typeof x === "object"
    ? Object.entries(x).map(([k, isi]) => `${k}: ${isi}`).join(" · ")
    : String(x));
  return Array.isArray(v) ? v.map(satu) : [satu(v)];
}

const KOLOM_CSV = [
  { kunci: "foc_number", judul: "Nomor" },
  { kunci: "recipient_name", judul: "Penerima" },
  { kunci: "store_id", judul: "Cabang" },
  { kunci: "purpose", judul: "Tujuan" },
  { kunci: "total_value_at_market_price", judul: "Nilai Pasar (IDR)" },
  { kunci: "status", judul: "Status" },
  { kunci: "created_at", judul: "Dibuat", ambil: (f) => formatDateTime(f.created_at) },
];

// KONTRAS-01 Batch 8 — peta `STATUS_BADGE` lokal yang dulu di sini (4 status
// dengan `cls:` warna keras) pindah ke `FOC_STATUS_TONE` di
// `components/etams/StatusBadge.jsx`. Label tidak berubah sedikit pun; yang
// berubah cuma sumber warnanya, dari palet keras jadi token tema.

export default function FocRequests() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Total dari SERVER: jumlah yang berhak dilihat, bukan panjang halaman.
  const [total, setTotal] = useState(0);
  // Cermin `URUT_SAH` di routers/foc_requests.py.
  const daftar = useDaftar(items, {
    server: { total, urutSah: ["created_at", "foc_number", "recipient_name", "approval_status"] },
    urutAwal: { kunci: "created_at", arah: "desc" },
    // Pola daftar seragam (27 Sep 2026): status tinggal di alamat halaman (`?status=`).
    saringan: { status: { label: "Status", param: "approval_status", tampil: (v) => LABEL_STATUS[v] || v } },
  });
  const { cari: q, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const filterStatus = daftar.nilaiSaringan.status || "ALL";

  const bangunParams = useCallback((ovr = {}) => {
    const p = { ...paramSaringan, limit, offset, sort, arah, ...ovr };
    if (q) p.cari = q;
    return p;
  }, [paramSaringan, q, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get("/foc-requests", { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // ⛔ Dulu daftar customer diambil TANPA parameter di sini — berhenti di 50
      //    customer pertama, jadi customer ke-51 dst. tidak pernah bisa dipilih di
      //    formulir FOC. Customer kini dicari di server lewat PemilihCari.
      const [foc, st] = await Promise.all([
        api.get("/foc-requests", { params: bangunParams() }),
        api.get("/stores"),
      ]);
      setItems(foc.data.items || []);
      setTotal(typeof foc.data.total === "number" ? foc.data.total : (foc.data.items || []).length);
      setStores(st.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Daftar FOC gagal dimuat.")); }
    finally { setLoading(false); }
  }, [bangunParams]);
  useEffect(() => { load(); }, [load]);

  const approve = async (foc) => {
    try { await api.post(`/foc-requests/${foc.id}/approve`, { note: null }); toast.success("Disetujui"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal menyetujui FOC.")); }
  };
  const submitReject = async () => {
    if (!rejectReason.trim()) { toast.error("Alasan wajib"); return; }
    try { await api.post(`/foc-requests/${rejectingId}/reject`, { rejection_reason: rejectReason }); toast.success("Ditolak"); setRejectingId(null); setRejectReason(""); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal menolak FOC.")); }
  };
  const storeName = (id) => stores.find((s) => s.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="foc-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Gift size={22} /> FOC (Barang Gratis)
            <Hint>Wajib persetujuan Owner. Owner yang mengajukan tetap harus disetujui Owner lain.</Hint>
          </h1>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="foc-new-btn"><Plus size={16} className="mr-1" /> Ajukan FOC</Button>
      </div>
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={filterStatus} onValueChange={(v) => daftar.setSaringan("status", v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="foc-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Status</SelectItem>
              <SelectItem value="PENDING">Menunggu Owner</SelectItem>
              <SelectItem value="APPROVED">Disetujui</SelectItem>
              <SelectItem value="REJECTED">Ditolak</SelectItem>
              <SelectItem value="CANCELLED">Dibatalkan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <PalangDaftar daftar={daftar} placeholder="Cari nomor / penerima / tujuan…" testId="foc-palang"
                      ekspor={{ nama: "foc", kolom: KOLOM_CSV, ambilHalaman }} />
        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Memuat...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <ThUrut daftar={daftar} kunci="foc_number" className="text-left px-3 py-2">Nomor</ThUrut>
                  <ThUrut daftar={daftar} kunci="recipient_name" className="text-left px-3 py-2">Penerima</ThUrut>
                  <th className="text-left px-3 py-2">Cabang</th>
                  <th className="text-left px-3 py-2">Tujuan</th>
                  <ThUrut daftar={daftar} kunci="total_value_at_market_price" className="text-right px-3 py-2" align="right">Nilai Pasar</ThUrut>
                  <ThUrut daftar={daftar} kunci="approval_status" className="text-left px-3 py-2">Status</ThUrut>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  {daftar.cari.trim() ? "Tidak ada yang cocok." : "Belum ada permintaan FOC."}
                </td></tr>}
                {daftar.tampil.map((f) => {
                  return (
                    <tr key={f.id} className="border-t border-border" data-testid={`foc-row-${f.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{f.foc_number}</td>
                      <td className="px-3 py-2">{f.recipient_name}</td>
                      <td className="px-3 py-2 text-xs">{storeName(f.store_id)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground max-w-xs truncate">{f.purpose}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{f.total_value_at_market_price ? `${formatIDR(f.total_value_at_market_price)}` : "—"}</td>
                      <td className="px-3 py-2"><FOCStatusBadge value={f.status} /></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => setDetail(f)} data-testid={`foc-detail-${f.id}`}>Detail</Button>
                        {isOwner && f.status === "PENDING_OWNER_APPROVAL" && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => approve(f)} className="text-success-soft-foreground" data-testid={`foc-approve-${f.id}`}>✓ Setujui</Button>
                            <Button variant="ghost" size="sm" onClick={() => setRejectingId(f.id)} className="text-danger-soft-foreground" data-testid={`foc-reject-${f.id}`}>✕ Tolak</Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="foc-paginasi" />
          </div>
        )}
      </Card>

      {showForm && <FocFormModal stores={stores} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {detail && (
        <Dialog open onOpenChange={(o) => { if (!o) setDetail(null); }}>
          <DialogContent data-testid="foc-detail-modal">
            <DialogHeader><DialogTitle>Detail FOC · {detail.foc_number}</DialogTitle></DialogHeader>
            <div className="space-y-2 text-sm">
              <div><strong>Penerima:</strong> {detail.recipient_name}</div>
              <div><strong>Cabang:</strong> {storeName(detail.store_id)}</div>
              <div><strong>Tujuan:</strong> {detail.purpose}</div>
              {detail.total_value_at_market_price && <div><strong>Nilai Pasar:</strong> {formatIDR(detail.total_value_at_market_price)}</div>}
              {detail.items_breakdown && <div><strong>Rincian Item:</strong><ul className="text-xs bg-muted/50 p-2 rounded mt-1 space-y-0.5">{rincianTeks(detail.items_breakdown).map((b, i) => <li key={i}>{b}</li>)}</ul></div>}
              <div><strong>Status:</strong> {FOC_STATUS_TONE[detail.status]?.l || detail.status}</div>
              {detail.rejection_reason && <div className="text-danger-soft-foreground"><strong>Alasan Penolakan:</strong> {detail.rejection_reason}</div>}
              <div className="text-xs text-muted-foreground mt-2">Diajukan: {formatDateTime(detail.created_at)}</div>
              {detail.approved_at && <div className="text-xs text-muted-foreground">Diproses: {formatDateTime(detail.approved_at)}</div>}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={!!rejectingId} onOpenChange={(o) => { if (!o) { setRejectingId(null); setRejectReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tolak FOC?</AlertDialogTitle>
            <AlertDialogDescription>Berikan alasan penolakan.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Alasan penolakan..." data-testid="foc-reject-reason" />
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={submitReject} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="foc-reject-submit">Tolak</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FocFormModal({ stores, onClose, onSaved }) {
  const [f, setF] = useState({ customer_id: "", store_id: "", recipient_name: "", purpose: "", total_value: "", notes: "" });
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.customer_id || !f.store_id || !f.recipient_name.trim() || !f.purpose.trim()) { toast.error("Isian wajib belum lengkap"); return; }
    setSaving(true);
    try {
      await api.post("/foc-requests", {
        customer_id: f.customer_id,
        store_id: f.store_id,
        recipient_name: f.recipient_name.trim(),
        purpose: f.purpose.trim(),
        total_value_at_market_price: f.total_value ? parseInt(f.total_value) : null,
        notes: f.notes || null,
      });
      toast.success("Diajukan (menunggu Owner)"); onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "FOC gagal diajukan.")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="foc-form-modal">
        <DialogHeader><DialogTitle>Ajukan FOC</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label>Customer *</Label>
            <PemilihCari
              value={customer}
              onChange={(c) => { setCustomer(c); setF((x) => ({ ...x, customer_id: c?.id || "" })); }}
              cari={cariCustomer}
              label={labelCustomer}
              keterangan={ketCustomer}
              placeholder="Pilih customer"
              placeholderCari="Ketik nama atau nomor telepon…"
              testId="foc-form-customer"
            />
          </div>
          <div className="col-span-2">
            <Label>Cabang *</Label>
            <Select value={f.store_id} onValueChange={(v) => setF({ ...f, store_id: v })}>
              <SelectTrigger data-testid="foc-form-store"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
              <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Nama Penerima *</Label><Input value={f.recipient_name} onChange={(e) => setF({ ...f, recipient_name: e.target.value })} placeholder="cth: PT Sukses Bersama" data-testid="foc-form-recipient" /></div>
          <div className="col-span-2"><Label>Tujuan / Alasan Bisnis *</Label><Textarea rows={2} value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} placeholder="cth: Hadiah CSR untuk mitra strategis..." data-testid="foc-form-purpose" /></div>
          <div className="col-span-2"><Label>Total Nilai Pasar (IDR, opsional)</Label><Input type="number" min={0} value={f.total_value} onChange={(e) => setF({ ...f, total_value: e.target.value })} placeholder="cth: 2500000" data-testid="foc-form-value" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="foc-form-save">{saving ? "..." : "Ajukan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
